// src/components/NonActiveAdReport.jsx

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdPreviewDialog from '../../components/AdPreviewDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const NonActiveAdReport = () => {
  const { can } = usePermissions();
  // State Variables
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState(0);
  const [page, setPage] = useState(0); // Zero-based index for DataGrid
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // Date Filters - Automatically set to current date and 30 days before
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);
    return thirtyDaysAgo;
  });
  const [endDate, setEndDate] = useState(new Date()); // Current date

  // Edit / Delete (privileges: ads.nonLive.edit / ads.nonLive.delete)
  const [editAd, setEditAd] = useState(null);
  const [editForm, setEditForm] = useState({ header: '', details: '', startDate: '', endDate: '', timeSlot: '', pincodes: '' });
  const [deleteAd, setDeleteAd] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } });
  const isoDay = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

  const openEdit = (row) => {
    setEditForm({
      header: row.rawHeader || '',
      details: row.rawDetails || '',
      startDate: isoDay(row.rawStart),
      endDate: isoDay(row.rawEnd),
      timeSlot: row.rawTimeSlot ?? '',
      pincodes: (row.pincodeList || []).join(', '),
    });
    setEditAd(row);
  };

  const submitEdit = async () => {
    if (!editForm.header.trim()) return showSnackbar('Header is required.', 'error');
    if (editForm.startDate && editForm.endDate && editForm.startDate > editForm.endDate) {
      return showSnackbar('Campaign start date cannot be after the end date.', 'error');
    }
    setActionBusy(true);
    try {
      await axios.put(`${API_BASE_URL}/ads/${editAd.id}`, {
        header: editForm.header.trim(),
        details: editForm.details,
        startDate: editForm.startDate || undefined,
        endDate: editForm.endDate || undefined,
        timeSlot: editForm.timeSlot === '' ? undefined : Number(editForm.timeSlot),
        pincodes: editForm.pincodes.split(',').map((p) => p.trim()).filter(Boolean),
      }, authHeaders());
      showSnackbar('Ad updated successfully.', 'success');
      setEditAd(null);
      fetchAds();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update ad.', 'error');
    }
    setActionBusy(false);
  };

  const confirmDelete = async () => {
    setActionBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/ads/${deleteAd.id}`, authHeaders());
      showSnackbar('Ad deleted successfully.', 'success');
      setDeleteAd(null);
      fetchAds();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete ad.', 'error');
    }
    setActionBusy(false);
  };

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch Ads Function
  const fetchAds = async () => {
    setLoading(true);
    try {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2); // Months are zero-indexed
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
      };

      const params = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        page: page + 1, // Backend is 1-based
        limit: pageSize,
      };

      const response = await axios.get(`${API_BASE_URL}/ads/inactive`, { params });

      if (response.status === 200) {
        const { ads, total } = response.data;

        // Check if ads are present
        if (ads.length === 0) {
          showSnackbar('No inactive ads found for the selected date range.', 'info');
        } else {
          showSnackbar(`Found ${total} inactive ads.`, 'success');
        }

        const mappedAds = ads.map((ad, index) => ({
          id: ad._id,
          slNo: (page * pageSize) + index + 1,
          header: ad.header || 'N/A',
          details: ad.details || 'N/A',
          startDate: ad.startDate ? new Date(ad.startDate).toLocaleDateString('en-GB') : 'N/A',
          endDate: ad.endDate ? new Date(ad.endDate).toLocaleDateString('en-GB') : 'N/A',
          createdAt: ad.createdAt ? new Date(ad.createdAt).toLocaleDateString('en-GB') : 'N/A',
          // Scheduled (starts in the future) / Expired are 'active' ads that are not running today
          status: ad.scheduleState === 'Scheduled'
            ? `Scheduled (starts ${ad.startDate ? new Date(ad.startDate).toLocaleDateString('en-GB') : 'N/A'})`
            : (ad.scheduleState || ad.status || 'N/A'),
          adType: ad.adType || 'N/A',
          providerName: ad.providerId?.name || 'N/A',
          timeSlot: ad.timeSlot || 'N/A',
          pincodes: Array.isArray(ad.pincodes) ? ad.pincodes.join(', ') : 'N/A',
          rawHeader: ad.header,
          rawDetails: ad.details,
          rawStart: ad.startDate,
          rawEnd: ad.endDate,
          rawTimeSlot: ad.timeSlot,
          pincodeList: Array.isArray(ad.pincodes) ? ad.pincodes : [],
          rawAd: ad,
        }));

        setAds(mappedAds);
        setTotalAds(total);
      } else {
        setAds([]);
        setTotalAds(0);
        showSnackbar('Failed to fetch ads.', 'error');
      }
    } catch (error) {
      setAds([]);
      setTotalAds(0);
      if (error.response?.data?.message) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        showSnackbar('Error fetching ads. Please try again.', 'error');
      }
    }
    setLoading(false);
  };

  // Snackbar Handlers
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Reset to Last 30 Days
  const resetToLast30Days = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);
    
    setStartDate(thirtyDaysAgo);
    setEndDate(now);
    setPage(0); // Reset pagination
  };

  // Print Handler
  const handlePrint = () => {
    if (!can('ads.nonLive.print')) return;
    if (ads.length === 0) {
      showSnackbar('No data to print.', 'error');
      return;
    }

    const doc = new jsPDF('landscape', 'pt', 'a4');

    // Title
    doc.setFontSize(16);
    doc.text('Non-Active Ad Report', doc.internal.pageSize.getWidth() / 2, 40, { align: 'center' });

    // Subtitle with Date Range
    doc.setFontSize(12);
    const dateRange = `From: ${startDate.toLocaleDateString('en-GB')} To: ${endDate.toLocaleDateString('en-GB')}`;
    doc.text(dateRange, doc.internal.pageSize.getWidth() / 2, 60, { align: 'center' });

    // Total count
    doc.setFontSize(10);
    doc.text(`Total Records: ${totalAds}`, doc.internal.pageSize.getWidth() / 2, 75, { align: 'center' });

    // Table Columns
    const tableColumn = [
      'Sl No', 
      'Header', 
      'Details', 
      'Ad Type',
      'Campaign Start', 
      'Campaign End', 
      'Created On',
      'Time Slot',
      'Provider Name',
      'Status'
    ];

    // Table Rows
    const tableRows = ads.map((ad) => [
      ad.slNo,
      ad.header.length > 20 ? ad.header.substring(0, 20) + '...' : ad.header,
      ad.details.length > 30 ? ad.details.substring(0, 30) + '...' : ad.details,
      ad.adType,
      ad.startDate,
      ad.endDate,
      ad.createdAt,
      ad.timeSlot,
      ad.providerName.length > 15 ? ad.providerName.substring(0, 15) + '...' : ad.providerName,
      ad.status,
    ]);

    // Add Table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 90,
      theme: 'grid',
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: { 
        fontSize: 8,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 35 },  // Sl No
        1: { cellWidth: 80 },  // Header
        2: { cellWidth: 100 }, // Details
        3: { cellWidth: 60 },  // Ad Type
        4: { cellWidth: 70 },  // Campaign Start
        5: { cellWidth: 70 },  // Campaign End
        6: { cellWidth: 70 },  // Created On
        7: { cellWidth: 50 },  // Time Slot
        8: { cellWidth: 80 },  // Provider Name
        9: { cellWidth: 60 },  // Status
      },
      margin: { top: 90, left: 20, right: 20 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString()}`, 
        20, 
        doc.internal.pageSize.getHeight() - 20
      );
      doc.text(
        `Page ${i} of ${pageCount}`, 
        doc.internal.pageSize.getWidth() - 100, 
        doc.internal.pageSize.getHeight() - 20
      );
    }

    // Save PDF
    doc.save(`NonActive_Ad_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    showSnackbar('Report downloaded successfully!', 'success');
  };

  // Fetch Ads on Mount and Dependency Change
  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, startDate, endDate]);

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br/><br/>
      
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px', maxWidth: '1400px', margin: 'auto' }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Non-Active Ad Report
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Showing inactive ads from {startDate.toLocaleDateString('en-GB')} to {endDate.toLocaleDateString('en-GB')}
              </Typography>

              <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>
                {/* Start Date Picker */}
                <Grid item xs={12} sm={6} md={2.5}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setStartDate(newValue);
                        setPage(0); // Reset to first page on filter change
                      }
                    }}
                    renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                    maxDate={endDate}
                  />
                </Grid>

                {/* End Date Picker */}
                <Grid item xs={12} sm={6} md={2.5}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setEndDate(newValue);
                        setPage(0); // Reset to first page on filter change
                      }
                    }}
                    renderInput={(params) => <TextField fullWidth {...params} size="small" />}
                    minDate={startDate}
                    maxDate={new Date()}
                  />
                </Grid>

                {/* Reset Button */}
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={resetToLast30Days}
                    fullWidth
                    size="small"
                  >
                    Last 30 Days
                  </Button>
                </Grid>

                {/* Print Button */}
                {can('ads.nonLive.print') && (
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                      fullWidth
                      disabled={ads.length === 0}
                      size="small"
                    >
                      Print Report
                    </Button>
                  </Grid>
                )}

                {/* Summary */}
                <Grid item xs={12} md={2.5}>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                    Total Records: {totalAds}
                  </Typography>
                </Grid>
              </Grid>

              {/* Data Grid */}
              {loading ? (
                <Grid container justifyContent="center" alignItems="center" style={{ height: '400px' }}>
                  <CircularProgress />
                  <Typography variant="body1" sx={{ ml: 2 }}>Loading ads...</Typography>
                </Grid>
              ) : (
                <div style={{ height: 650, width: '100%' }}>
                  <DataGrid
                    rows={ads}
                    columns={[
                      { field: 'slNo', headerName: 'Sl No', width: 70, align: 'center' },
                      {
                        field: 'preview', headerName: 'Preview', width: 110, sortable: false, filterable: false,
                        renderCell: (params) => (
                          <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setPreviewAd(params.row.rawAd)}>
                            View
                          </Button>
                        ),
                      },
                      { field: 'header', headerName: 'Header', width: 150 },
                      { field: 'details', headerName: 'Details', width: 200 },
                      { field: 'adType', headerName: 'Ad Type', width: 100 },
                      { field: 'startDate', headerName: 'Campaign Start', width: 120 },
                      { field: 'endDate', headerName: 'Campaign End', width: 120 },
                      { field: 'createdAt', headerName: 'Created On', width: 120 },
                      { field: 'timeSlot', headerName: 'Time Slot (s)', width: 100, align: 'center' },
                      { field: 'providerName', headerName: 'Provider Name', width: 150 },
                      { field: 'status', headerName: 'Status', width: 190, align: 'center' },
                      { 
                        field: 'pincodes', 
                        headerName: 'Pincodes', 
                        width: 280,
                        renderCell: (params) => (
                          <div style={{ 
                            whiteSpace: 'normal', 
                            wordWrap: 'break-word',
                            lineHeight: '1.2',
                            padding: '8px 0',
                            fontSize: '0.875rem'
                          }}>
                            {params.value && params.value !== 'N/A' 
                              ? params.value.split(', ').map((pincode, index) => (
                                  <span key={index} style={{ 
                                    display: 'inline-block',
                                    margin: '1px 2px',
                                    padding: '2px 6px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    border: '1px solid #bbdefb'
                                  }}>
                                    {pincode.trim()}
                                  </span>
                                ))
                              : 'N/A'
                            }
                          </div>
                        )
                      },
                      ...((can('ads.nonLive.edit') || can('ads.nonLive.delete')) ? [{
                        field: 'actions',
                        headerName: 'Actions',
                        width: 110,
                        sortable: false,
                        filterable: false,
                        renderCell: (params) => (
                          <>
                            {can('ads.nonLive.edit') && (
                              <Tooltip title="Edit ad">
                                <IconButton size="small" color="primary" onClick={() => openEdit(params.row)}><EditIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                            {can('ads.nonLive.delete') && (
                              <Tooltip title="Delete ad">
                                <IconButton size="small" color="error" onClick={() => setDeleteAd(params.row)}><DeleteIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </>
                        ),
                      }] : []),
                    ]}
                    page={page}
                    pageSize={pageSize}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    rowCount={totalAds}
                    pagination
                    paginationMode="server"
                    onPageChange={(newPage) => setPage(newPage)}
                    onPageSizeChange={(newPageSize) => {
                      setPageSize(newPageSize);
                      setPage(0); // Reset to first page
                    }}
                    disableSelectionOnClick
                    getRowHeight={() => 'auto'}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                      },
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 'unset !important',
                        maxHeight: 'none !important',
                        whiteSpace: 'normal',
                      },
                      '& .MuiDataGrid-row': {
                        maxHeight: 'none !important',
                      },
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit ad */}
          <AdPreviewDialog ad={previewAd} onClose={() => setPreviewAd(null)} />

          <Dialog open={!!editAd} onClose={() => !actionBusy && setEditAd(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Ad</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}><TextField fullWidth label="Header" value={editForm.header} onChange={(e) => setEditForm({ ...editForm, header: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Details" value={editForm.details} onChange={(e) => setEditForm({ ...editForm, details: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Campaign Start" InputLabelProps={{ shrink: true }} value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Campaign End" InputLabelProps={{ shrink: true }} value={editForm.endDate} onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Time Slot (s)" value={editForm.timeSlot} onChange={(e) => setEditForm({ ...editForm, timeSlot: e.target.value })} /></Grid>
                <Grid item xs={12} sm={8}><TextField fullWidth label="Pincodes (comma separated)" value={editForm.pincodes} onChange={(e) => setEditForm({ ...editForm, pincodes: e.target.value })} /></Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditAd(null)} disabled={actionBusy}>Cancel</Button>
              <Button variant="contained" onClick={submitEdit} disabled={actionBusy}>{actionBusy ? 'Saving...' : 'Save changes'}</Button>
            </DialogActions>
          </Dialog>

          {/* Delete ad */}
          <Dialog open={!!deleteAd} onClose={() => !actionBusy && setDeleteAd(null)}>
            <DialogTitle>Delete Ad</DialogTitle>
            <DialogContent>
              <Typography>Delete "{deleteAd && deleteAd.header}" permanently? This cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteAd(null)} disabled={actionBusy}>Cancel</Button>
              <Button color="error" variant="contained" onClick={confirmDelete} disabled={actionBusy}>{actionBusy ? 'Deleting...' : 'Delete'}</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for Notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default NonActiveAdReport;